import { addToRecord, updateRecord } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import {
  ThreadRepliesLayoutAction,
  ThreadStarterAttachmentsLayoutAction,
  ThreadStarterBodyLayoutAction,
} from '../../domains/threadSetLayout/actions.types';

import {
  ThreadLayout,
  ThreadLayouts,
  defaultRepliesLayout,
  defaultThreadStarterAttachmentsLayout,
  defaultThreadStarterBodyLayout,
} from './types';

export type ThreadLayoutsAction =
  | ThreadStarterBodyLayoutAction
  | ThreadStarterAttachmentsLayoutAction
  | ThreadRepliesLayoutAction;

const initialState: ThreadLayouts = {};

const defaultLayout = {
  threadStarterBody: defaultThreadStarterBodyLayout,
  threadStarterAttachments: defaultThreadStarterAttachmentsLayout,
  replies: defaultRepliesLayout,
};

const areLayoutsEqual = (a: Omit<ThreadLayout, 'id'>, b: Omit<ThreadLayout, 'id'>) =>
  a.threadStarterBody === b.threadStarterBody &&
  a.threadStarterAttachments === b.threadStarterAttachments &&
  a.replies === b.replies;

const addOrUpdateThreadLayout = (
  state: ThreadLayouts,
  threadId: ThreadLayout['id'],
  newLayout: Partial<ThreadLayout>
) => {
  const existingLayout = state[threadId];

  if (existingLayout == null) {
    const updatedLayout = { id: threadId, ...defaultLayout, ...newLayout };
    if (areLayoutsEqual(updatedLayout, defaultLayout)) {
      return state;
    }

    return addToRecord(state, threadId, updatedLayout);
  }

  const updatedLayout = { ...existingLayout, ...newLayout };

  if (areLayoutsEqual(updatedLayout, existingLayout)) {
    return state;
  }

  return updateRecord(state, threadId, updatedLayout);
};

const setThreadStarterBodyLayout = (
  state: ThreadLayouts,
  { threadId, layout }: ThreadStarterBodyLayoutAction['payload']
) =>
  addOrUpdateThreadLayout(state, threadId, {
    threadStarterBody: layout,
  });

const setThreadStarterAttachmentsLayout = (
  state: ThreadLayouts,
  { threadId, layout }: ThreadStarterAttachmentsLayoutAction['payload']
) =>
  addOrUpdateThreadLayout(state, threadId, {
    threadStarterAttachments: layout,
  });

const setThreadRepliesLayout = (state: ThreadLayouts, { threadId, layout }: ThreadRepliesLayoutAction['payload']) =>
  addOrUpdateThreadLayout(state, threadId, {
    replies: layout,
  });

const reducer: Reducer<ThreadLayouts, ThreadLayoutsAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'THREAD_STARTER.BODY_LAYOUT_SET':
      return setThreadStarterBodyLayout(state, action.payload);
    case 'THREAD_STARTER.ATTACHMENTS_LAYOUT_SET':
      return setThreadStarterAttachmentsLayout(state, action.payload);
    case 'THREAD.REPLIES_LAYOUT_SET':
      return setThreadRepliesLayout(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
